import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import ordersService from "../../../../services/orders.service";
import { OrderModel } from "../../../../pages/shared/models/order/order-item.model";
import FirstStep from "./blocks/first-step";
import SecondStep from "./blocks/second-step";
import { CurrencyModel } from "../../../../redux/reducers/static-data.reducers";
import { calculateForLand } from "../../../../pages/calculate/calculate-functions";
import { TrackingRecord } from "../../../../pages/shared/models/order/trackingRecord.model";
import { Product } from "../../../../pages/shared/models/order/product.model";
import { getProductStores } from "../../../../redux/slices/static-data.slice";
import { AxiosError } from "axios";
import { mapValidationErrors } from "../../../../pages/user/settings/personal-info/personal-info.service";
import { getMessage, initToast } from "../../../../pages/shared/helper-functions";
import { MessageSections } from "../../../../pages/components/message/message";

const AddProduct = ({ closePopup }: AddProductDialogProps) => {
  const { currentRate, currencies } = useAppSelector(
    (state) => state.staticDataReducer,
  );
  const { currentUser } = useAppSelector((state) => state.currentUserReducer);

  const [updateData, setUpdateData] = useState({
    weight: 0,
    width: 0,
    length: 0,
    height: 0,
    notes: "",
    addressLine2: "",
    trackingNumber: "",
    shippingPrice: 0,
    currency: currencies.find((currency) => currency.currencyId === 1),
    product: [] as Product[],
  } as OrderUpdateDataModel);
  const [order, setOrder] = useState<OrderModel>();
  const [volume, setVolume] = useState<number>();
  const [step, setStep] = useState(ProductSearchStep.initial);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { languageId, messageCache } = useAppSelector((state) => state.messagesReducer);

  const [isReady, setIsReady] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    Promise.allSettled([dispatch(getProductStores({ limit: 100 }))]).then(() => {
      setIsReady(true);
    });
  }, []);

  function passedValidations() {
    const errors = {} as { [key: string]: string };
    const product = updateData.product[0];

    if (!updateData.weight) {
      errors["weight"] = "required";
    }

    if (!order) {
      if (!product || !product.store) {
        errors["store"] = "required";
      }
      if (
        !product ||
        !product.store ||
        (product.store.isOther && !product.storeName)
      ) {
        errors["storeName"] = "required";
      }
    }

    setErrors(errors);

    return !Object.keys(errors).length;
  }

  function onProductSave() {
    if (passedValidations()) {
      const product = updateData.product[0];

      const serializedUpdateData = {
        ...updateData,
        currency: updateData.currency.currencyId,
        product: product && [
          {
            store: product.store?.storeId,
            storeName: product.storeName[1],
          },
        ],
      };

      setIsLoading(true);

      if (!order) {
        ordersService
          .createUnannouncedOrder(serializedUpdateData, languageId)
          .then((response) => {
            closePopup();
          })
          .catch((error) => {

            if (error?.reason === 'already_exists_in_warehouse') {
              initToast(
                getMessage('order_in_other_state', MessageSections.ORDERS, messageCache, languageId),
                'error'
              )
            }

            if (error instanceof AxiosError) {
              setErrors(mapValidationErrors(error));
            }
            setIsLoading(false);
          });
      } else {
        ordersService
          .updateByTrackingNumber(serializedUpdateData, languageId)
          .then((response) => {
            closePopup();
          })
          .catch((reason) => {
            if (reason instanceof AxiosError) {
              setErrors(mapValidationErrors(reason));
            }
            setIsLoading(false);
          });
      }
    }
  }

  function nextStep() {
    if (!updateData.trackingNumber) {
      setErrors({ trackingNumber: "required" });
    } else {
      // dispatch(setIsLoading(true))
      setErrors({});

      ordersService
        .getOrderByGivenFilterCriteria({
          products: { trackingNumber: updateData.trackingNumber },
        })
        .then((order) => {
          const orderTrackRecords =
            order?.trackRecord || ([] as TrackingRecord[]);

          if (!orderTrackRecords[0] || orderTrackRecords[0].state.isDraft) {
            setOrder(order);

            if (order?.products[0] && !!order.products[0].description) {
              setUpdateData((prevState) => {
                prevState.notes = order.products[0].description;
                return prevState;
              });
            }

            setStep(ProductSearchStep.second);
          } else {
            setErrors({ trackingNumber: "order_already_added" });
          }
        })
        .catch((reason) => {
          setOrder(undefined);
          setStep(ProductSearchStep.second);
        })
        .finally(() => {
          // dispatch(setIsLoading(false));
        });
    }
  }

  function prevStep() {
    setUpdateData({
      weight: 0,
      width: 0,
      length: 0,
      height: 0,
      notes: "",
      comment: "",
      addressLine2: "",
      trackingNumber: "",
      shippingPrice: 0,
      currency: currencies.find(
        (currency) => currency.currencyId === 1,
      ) as CurrencyModel,
      product: [] as Product[],
      crmListId: null,
    });
    setVolume(undefined);
    setErrors({});
    setOrder(undefined);
    setStep(ProductSearchStep.initial);
  }

  function onChange(field: string, value: any) {
    setUpdateData((prevState) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      prevState[field] = value;

      if (
        field === "weight" ||
        field === "length" ||
        field === "height" ||
        field === "length"
      ) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const eurRate = +currentRate.Rates.ExchangeRate.find(
          (rate) => rate.ISO.toLowerCase() === "eur",
        ).Rate;
        const volume =
          prevState["width"] * prevState["height"] * prevState["length"];
        const price = currentUser?.warehouse
          ? calculateForLand(
            prevState["weight"],
            volume,
            currentUser.warehouse,
            prevState["currency"],
            eurRate,
          )
          : { price: 0, volumetricPrice: 0 };

        prevState["shippingPrice"] = Math.max(
          price.price,
          price.volumetricPrice,
        );
      }

      if (field === "store") {
        const clonedProduct = { ...prevState["product"][0] } as Product;
        clonedProduct.store = value;
        if (!value.isOther) {
          clonedProduct.storeName = value.name;
        }
        prevState["product"] = [clonedProduct];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete prevState["store"];
      }

      if (field === "storeName") {
        const clonedProduct = { ...prevState["product"][0] } as Product;
        clonedProduct.storeName = value;
        prevState["product"] = [clonedProduct];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete prevState["storeName"];
      }

      return { ...prevState };
    });

    if (updateData.width && updateData.length && updateData.height) {
      setVolume(
        (updateData.width || 0) *
        (updateData.height || 0) *
        (updateData.length || 0),
      );
    }

    if (errors[field]) {
      setErrors((prevState) => {
        delete prevState[field];
        return prevState;
      });
    }
  }

  if (isReady) {
    if (step === ProductSearchStep.initial) {
      return (
        <FirstStep
          trackingNumber={updateData.trackingNumber}
          errors={errors}
          onChange={onChange}
          nextStep={nextStep}
          closePopup={closePopup}
        />
      );
    } else {
      return (
        <SecondStep
          trackingNumber={updateData.trackingNumber}
          order={order}
          volume={volume}
          updateData={updateData}
          errors={errors}
          onChange={onChange}
          nextStep={nextStep}
          prevStep={prevStep}
          closePopup={closePopup}
          isLoading={isLoading}
          onProductSave={onProductSave}
        />
      );
    }
  } else {
    return <></>;
  }
};

interface AddProductDialogProps {
  closePopup: () => void;
}

enum ProductSearchStep {
  initial,
  second,
}

export interface OrderUpdateDataModel {
  weight: number;
  width: number;
  length: number;
  height: number;
  notes: string;
  comment: string;
  shippingPrice: number;
  addressLine2: string;
  trackingNumber: string;
  currency: CurrencyModel;
  product: Product[];
  crmListId: number | null;
}

export default AddProduct;
